import React, { useState } from "react";
import { Mail, Phone, MapPin, MessageCircle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";

const WEB3FORMS_ACCESS_KEY = "06937bdf-e3bd-42bc-bd09-b0ddd3934c1b";
const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";

const initial = {
  name: "", company: "", email: "", phone: "", subject: "", message: "",
};

const SUBJECTS_TR = [
  "KKDİK Madde Kayıt",
  "Ön-MBDF Süreçleri",
  "SEA Sınıflandırma",
  "SDS / GBF Hazırlama",
  "UFI / PCN Bildirimi",
  "Kimyasal Envanter",
  "Teknik Dosya",
  "Diğer",
];

const SUBJECTS_EN = [
  "KKDİK Substance Registration",
  "Pre-MBDF Processes",
  "SEA Classification",
  "SDS Preparation",
  "UFI / PCN Notification",
  "Chemical Inventory",
  "Technical Dossier",
  "Other",
];

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const { language, t } = useLanguage();
  const SUBJECTS = language === "en" ? SUBJECTS_EN : SUBJECTS_TR;

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("contact.errorRequired"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(WEB3FORMS_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: `${t("contact.emailSubjectPrefix")} — ${form.subject || t("contact.emailSubjectGeneral")}`,
          from_name: form.name,
          name: form.name,
          company: form.company,
          email: form.email,
          phone: form.phone,
          konu: form.subject,
          message: form.message,
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(t("contact.success"));
        setForm(initial);
      } else {
        toast.error(t("contact.errorSend"));
      }
    } catch (err) {
      toast.error(t("contact.errorGeneric"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-white">
      <section className="relative pt-24 pb-12 lg:pt-32 lg:pb-16">
        <div className="absolute inset-0 hex-pattern opacity-100 pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.22em] text-teal-700 mb-4">{t("contact.kicker")}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-slate-900 tracking-tight max-w-3xl">
            {t("contact.titlePrefix")} <em className="not-italic text-teal-700">{t("contact.titleEm")}</em> {t("contact.titleSuffix")}
          </h1>
          <p className="mt-5 text-slate-600 max-w-2xl">
            {t("contact.lead")}
          </p>
        </div>
      </section>

      <section className="pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Contact info */}
          <aside className="lg:col-span-5 space-y-5" data-testid="contact-info">
            <div className="p-7 rounded-2xl bg-[#F0FDFA] border border-teal-100">
              <h3 className="font-display text-2xl text-slate-900">{t("contact.directTitle")}</h3>
              <p className="mt-2 text-sm text-slate-600">
                {t("contact.directLead")}
              </p>

              <ul className="mt-6 space-y-4">
                <li className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-white border border-teal-100 flex items-center justify-center text-teal-700">
                    <Mail size={17} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500">{t("contact.email")}</div>
                    <a href="mailto:info@maddekayit.com" className="text-slate-900 font-medium hover:text-teal-700">
                      info@maddekayit.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-white border border-teal-100 flex items-center justify-center text-teal-700">
                    <Phone size={17} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500">{t("contact.phone")}</div>
                    <a href="tel:+902128120295" className="text-slate-900 font-medium hover:text-teal-700">
                      0212 812 02 95
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-white border border-teal-100 flex items-center justify-center text-teal-700">
                    <MessageCircle size={17} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500">{t("contact.whatsapp")}</div>
                    <a
                      href="https://wa.me/902123333333"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-900 font-medium hover:text-teal-700"
                    >
                      {t("contact.whatsappCta")}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-white border border-teal-100 flex items-center justify-center text-teal-700">
                    <MapPin size={17} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500">{t("contact.location")}</div>
                    <div className="text-slate-900 font-medium">{t("contact.locationValue")}</div>
                    <div className="text-sm text-slate-500">{t("contact.remoteNote")}</div>
                  </div>
                </li>
              </ul>
            </div>

            <div className="p-7 rounded-2xl border border-slate-200">
              <h4 className="font-display text-xl text-slate-900">{t("contact.hoursTitle")}</h4>
              <div className="mt-3 text-sm text-slate-600 space-y-1.5">
                <div className="flex justify-between"><span>{t("contact.monFri")}</span><span>08:30 – 17:30</span></div>
                <div className="flex justify-between"><span>{t("contact.sat")}</span><span>08:30 – 17:30</span></div>
                <div className="flex justify-between"><span>{t("contact.sun")}</span><span>08:30 – 17:30</span></div>
              </div>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              className="p-7 lg:p-10 rounded-2xl border border-slate-200 bg-white"
              data-testid="contact-form"
            >
              <h3 className="font-display text-2xl text-slate-900">{t("contact.formTitle")}</h3>
              <p className="mt-1 text-sm text-slate-600">
                {t("contact.formLead")}
              </p>

              <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs uppercase tracking-[0.16em] text-slate-500 mb-2">
                    {t("contact.fieldName")}
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    required
                    placeholder={t("contact.fieldNamePlaceholder")}
                    className="w-full h-12 px-4 rounded-lg border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100 transition"
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.16em] text-slate-500 mb-2">
                    {t("contact.fieldCompany")}
                  </label>
                  <input
                    type="text"
                    value={form.company}
                    onChange={update("company")}
                    placeholder={t("contact.fieldCompanyPlaceholder")}
                    className="w-full h-12 px-4 rounded-lg border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100 transition"
                    data-testid="contact-input-company"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.16em] text-slate-500 mb-2">
                    {t("contact.fieldEmail")}
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    required
                    placeholder={t("contact.fieldEmailPlaceholder")}
                    className="w-full h-12 px-4 rounded-lg border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100 transition"
                    data-testid="contact-input-email"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.16em] text-slate-500 mb-2">
                    {t("contact.fieldPhone")}
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder={t("contact.fieldPhonePlaceholder")}
                    className="w-full h-12 px-4 rounded-lg border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100 transition"
                    data-testid="contact-input-phone"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs uppercase tracking-[0.16em] text-slate-500 mb-2">
                    {t("contact.fieldSubject")}
                  </label>
                  <select
                    value={form.subject}
                    onChange={update("subject")}
                    className="w-full h-12 px-4 rounded-lg border border-slate-200 bg-white text-slate-900 focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100 transition"
                    data-testid="contact-input-subject"
                  >
                    <option value="">{t("contact.fieldSubjectPlaceholder")}</option>
                    {SUBJECTS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs uppercase tracking-[0.16em] text-slate-500 mb-2">
                    {t("contact.fieldMessage")}
                  </label>
                  <textarea
                    value={form.message}
                    onChange={update("message")}
                    required
                    rows={6}
                    placeholder={t("contact.fieldMessagePlaceholder")}
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100 transition resize-y"
                    data-testid="contact-input-message"
                  />
                </div>
              </div>

              <div className="mt-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <p className="text-xs text-slate-500">
                  {t("contact.consentNote")}
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-teal-700 text-white text-sm font-medium hover:bg-teal-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="contact-submit-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> {t("contact.sending")}
                    </>
                  ) : (
                    <>
                      {t("contact.submit")} <Send size={15} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
